import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Sparkles } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";

const MAX = 3;

/** Grid 3 slot foto produk: unggah, hapus, jadikan utama. */
export const ProductPhotos = ({ form, setForm, editing, onSaved }) => {
  const { t } = useI18n();
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const images = form.images || [];

  const upload = async (input) => {
    const file = input.files?.[0];
    if (!file) return;
    if (images.length >= MAX) {
      toast.error(`Maksimal ${MAX} foto per produk`);
      input.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => {
        const list = [...(f.images || []), data.url].slice(0, MAX);
        return { ...f, images: list, image_url: list[0] };
      });
      toast.success(t("photo_saved"));
    } catch (err) {
      toast.error(errMsg(err, "Gagal mengunggah foto"));
    } finally {
      setUploading(false);
      input.value = "";
    }
  };

  const makePrimary = (i) =>
    setForm((f) => {
      const list = [...(f.images || [])];
      [list[0], list[i]] = [list[i], list[0]];
      return { ...f, images: list, image_url: list[0] };
    });

  const remove = (i) =>
    setForm((f) => {
      const list = (f.images || []).filter((_, j) => j !== i);
      return { ...f, images: list, image_url: list[0] || "" };
    });

  const aiFill = async () => {
    if (
      !form.image_url &&
      !window.confirm("Produk ini belum ada foto. Buat foto dengan AI juga? (memakai kredit AI)")
    )
      return;
    setAiBusy(true);
    try {
      const { data } = await api.post(`/products/${editing}/autofill`, {
        description: true,
        description_ja: true,
        name_ja: true,
        photo: !form.image_url,
        overwrite: true,
      });
      setForm((f) => ({
        ...f,
        description: data.description || f.description,
        description_ja: data.description_ja || f.description_ja,
        name_ja: data.name_ja || f.name_ja,
        image_url: data.image_url || f.image_url,
        images: data.images?.length ? data.images : f.images,
      }));
      toast.success("Deskripsi dibuat oleh AI");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Gagal membuat deskripsi"));
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">
          Foto produk (maksimal {MAX}, foto pertama jadi utama)
        </p>
        <div className="grid grid-cols-3 gap-2" data-testid="product-photo-grid">
          {[0, 1, 2].map((i) => {
            const url = images[i];
            return (
              <div
                key={i}
                className="relative aspect-square rounded-xl bg-gray-100 overflow-hidden border border-gray-200"
                data-testid={`product-photo-slot-${i}`}
              >
                {url ? (
                  <>
                    <img src={url} alt="" className="h-full w-full object-cover" />
                    {i === 0 && (
                      <span className="absolute top-1 left-1 rounded-full bg-gray-900/80 px-2 py-0.5 text-[10px] font-medium text-white">
                        Utama
                      </span>
                    )}
                    {i > 0 && (
                      <button
                        data-testid={`product-photo-primary-${i}`}
                        className="absolute bottom-1 left-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-medium hover:bg-white"
                        onClick={() => makePrimary(i)}
                      >
                        Jadikan utama
                      </button>
                    )}
                    <button
                      data-testid={`product-photo-remove-${i}`}
                      className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-white/90 text-destructive hover:bg-white"
                      onClick={() => remove(i)}
                    >
                      <X size={13} />
                    </button>
                  </>
                ) : (
                  <label
                    className="h-full w-full grid place-items-center text-gray-400 cursor-pointer hover:bg-gray-200/60"
                    data-testid={`product-photo-add-label-${i}`}
                  >
                    <ImagePlus size={18} />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`product-photo-file-input-${i}`}
                      disabled={uploading}
                      onChange={(e) => upload(e.target)}
                    />
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <label
        className="flex items-center justify-center gap-2 h-10 rounded-xl border border-gray-200 text-sm font-medium cursor-pointer hover:bg-gray-50"
        data-testid="upload-photo-label"
      >
        <ImagePlus size={16} />
        {uploading ? t("uploading") : t("upload_photo")}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid="product-photo-file-input"
          disabled={uploading || images.length >= MAX}
          onChange={(e) => upload(e.target)}
        />
      </label>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        data-testid="ai-fill-description-btn"
        disabled={aiBusy || !editing}
        onClick={aiFill}
      >
        <Sparkles size={14} className="mr-1" />
        {aiBusy ? "Membuat…" : "Isi deskripsi & foto dengan AI"}
      </Button>
      {!editing && (
        <p className="text-xs text-muted-foreground">
          Simpan produk dulu, lalu buka lagi untuk memakai bantuan AI.
        </p>
      )}
    </div>
  );
};
